import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetSenderById } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import {
	Card,
	Icon,
	Skeleton,
	TabItem,
	Tabs,
	TabsList,
	Typography,
} from "@st-marketplace/ui";
import { type FC, useState } from "react";
import SendersSingleAction from "../SendersSingleAction";
import SenderInfoDetails from "./Details";
import SenderInfoTransactions from "./Transactions";
import SenderInfoTransferList from "./TransferList";

interface SenderInfoProps {
	senderId: string;
}

const SenderInfo: FC<SenderInfoProps> = ({ senderId }) => {
	const [tabValue, setTabValue] = useState("0");

	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();

	const {
		data: senderData,
		error: getSenderByIdError,
		isLoading,
		refetch,
	} = useGetSenderById({
		path: { sender_id: senderId },
	});

	const error = getSenderByIdError
		? getErrorInterface(getSenderByIdError)
		: null;

	return (
		<div className="flex flex-col gap-6 p-4">
			<div className="flex flex-wrap items-center justify-between gap-4">
				<div>
					<Typography variant="title-md" as="h1" className="mb-1">
						{t("senderInfo.title")}
					</Typography>
					<div className="mb-2 [&>p]:mb-1">
						{isLoading ? (
							<Skeleton className="h-5 w-50" />
						) : (
							<div className="flex flex-col gap-2 md:flex-row">
								<Typography
									variant="body-sm"
									className="text-secondary-text-100"
								>
									{senderData?.ea_email}
								</Typography>
								<Typography
									variant="body-sm"
									className="text-secondary-text-100"
								>
									{t("senderInfo.startedAt")}:{" "}
									{senderData?.created_at &&
										getTimeSinceText(senderData.created_at, t, lang)}
								</Typography>
							</div>
						)}
					</div>
				</div>

				{senderData && (
					<Card className="rounded-md">
						<SendersSingleAction selectedSender={senderData} />
					</Card>
				)}
			</div>

			<Tabs value={tabValue} onValueChange={setTabValue}>
				<TabsList>
					<TabItem value="0" Icon={<Icon name="information-line" />}>
						{t("senderInfo.tabInfo")}
					</TabItem>
					<TabItem value="1" Icon={<Icon name="exchange-line" />}>
						{t("senderInfo.tabTransactions")}
					</TabItem>
					<TabItem value="2" Icon={<Icon name="store-2-line" />}>
						{t("senderInfo.tabTransferList")}
					</TabItem>
				</TabsList>
			</Tabs>

			{tabValue === "0" && (
				<SenderInfoDetails
					senderId={senderId}
					data={senderData}
					loading={isLoading}
					error={error}
					refetch={refetch}
				/>
			)}
			{tabValue === "1" && <SenderInfoTransactions senderId={senderId} />}
			{tabValue === "2" && <SenderInfoTransferList senderId={senderId} />}
		</div>
	);
};

export default SenderInfo;
